import React, { useState, useEffect, useRef } from 'react';
import { guardarSesion } from '../../ui/sesion.js';
// Sistema de diseño de la suite: solo los tokens de color. La tarjeta del
// formulario se repinta con ellos; el fondo animado de canvas se queda igual.
import { C } from '../../ui/theme.js';

const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
// Clave `anon` vigente del proyecto CRM (rotada en diciembre de 2025). La copia
// que había aquí era la de diciembre de 2024; funcionaba todavía, pero es la
// misma que ya dio "Invalid API key" en otros repos del grupo. Se unifica con
// la que usan el hub CRM y la app SEO/GEO para no volver a tener dos verdades.
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

const login = async (email, password) => {
  try {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json();
    if (data.error) return { success: false, error: data.error_description || data.error.message };
    const usersRes = await fetch(
      `${SUPABASE_URL}/rest/v1/campanas_usuarios?email=eq.${email}&select=*`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } }
    );
    const users = await usersRes.json();
    const user = users?.length > 0 ? { ...users[0], email } : { email, nombre: email.split('@')[0], rol: 'usuario' };

    // Sesión compartida con el resto de apps de *.dimoauto.com: con esto el
    // CRM y las suyas ya no vuelven a pedir la contraseña.
    guardarSesion({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
      email,
      nombre: user.nombre,
      rol: user.rol,
    });

    return { success: true, user };
  } catch (e) {
    return { success: false, error: 'Error de conexión con el servidor' };
  }
};

// ==================== ANIMATED LOGIN BACKGROUND (Canvas) ====================
const AnimatedLoginBackground = React.forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef({
    nodes: [],
    clickBoost: 0,
    initialized: false
  });
  
  // Exponer función triggerPulse al padre
  React.useImperativeHandle(ref, () => ({
    triggerPulse: () => {
      stateRef.current.clickBoost = 1;
    }
  }));
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let width, height;
    
    // Configuración
    const numNodes = 80;
    const connectionDistance = 180;
    const nodeSpeed = 0.3;
    
    // Crear nodos
    const createNodes = () => {
      const nodes = [];
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * nodeSpeed,
          vy: (Math.random() - 0.5) * nodeSpeed,
          radius: 2 + Math.random() * 2,
          baseRadius: 2 + Math.random() * 2
        });
      }
      return nodes;
    };
    
    // Configurar canvas
    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      
      // Recrear nodos si es necesario
      if (stateRef.current.nodes.length === 0) {
        stateRef.current.nodes = createNodes();
      }
    };
    
    setup();
    window.addEventListener('resize', setup);
    
    // Loop de animación
    const animate = () => {
      const state = stateRef.current;
      const nodes = state.nodes;
      
      // Decay del click boost
      if (state.clickBoost > 0) {
        state.clickBoost = Math.max(0, state.clickBoost - 0.015);
      }
      
      // Fondo gradiente blanco/gris suave (invertido)
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, '#f0f4f8');
      bgGradient.addColorStop(0.5, '#e8f0f7');
      bgGradient.addColorStop(1, '#f5f7fa');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);
      
      // Actualizar posiciones de nodos
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        
        // Mover nodo
        node.x += node.vx;
        node.y += node.vy;
        
        // Rebotar en los bordes suavemente
        if (node.x < 0) { node.x = 0; node.vx *= -1; }
        if (node.x > width) { node.x = width; node.vx *= -1; }
        if (node.y < 0) { node.y = 0; node.vy *= -1; }
        if (node.y > height) { node.y = height; node.vy *= -1; }
        
        // Actualizar radio con boost
        node.radius = node.baseRadius * (1 + state.clickBoost * 0.5);
      }
      
      // Dibujar conexiones entre nodos cercanos
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < connectionDistance) {
            // Opacidad basada en distancia (más cerca = más opaco)
            const opacity = (1 - distance / connectionDistance) * 0.6;
            const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3);
            
            // Gradiente de línea
            const gradient = ctx.createLinearGradient(
              nodes[i].x, nodes[i].y,
              nodes[j].x, nodes[j].y
            );
            gradient.addColorStop(0, `rgba(0, 81, 135, ${boostedOpacity})`);
            gradient.addColorStop(0.5, `rgba(77, 130, 188, ${boostedOpacity * 0.8})`);
            gradient.addColorStop(1, `rgba(0, 81, 135, ${boostedOpacity})`);
            
            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Dibujar nodos
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        
        // Glow exterior
        const glowRadius = node.radius * 4;
        const glowGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, glowRadius
        );
        const glowIntensity = 0.12 + state.clickBoost * 0.15;
        glowGradient.addColorStop(0, `rgba(0, 81, 135, ${glowIntensity})`);
        glowGradient.addColorStop(0.5, `rgba(77, 130, 188, ${glowIntensity * 0.3})`);
        glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)');
        
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // Nodo central azul (invertido: antes era blanco)
        const nodeGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius
        );
        nodeGradient.addColorStop(0, `rgba(77, 130, 188, ${0.95 + state.clickBoost * 0.05})`);
        nodeGradient.addColorStop(0.3, `rgba(0, 81, 135, ${0.85 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(1, `rgba(0, 55, 100, ${0.7})`);
        
        ctx.fillStyle = nodeGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Glow detrás de la tarjeta
      if (cardRect) {
        const cardCenterX = cardRect.x + cardRect.width / 2;
        const cardCenterY = cardRect.y + cardRect.height / 2;
        const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8;
        
        const cardGlow = ctx.createRadialGradient(
          cardCenterX, cardCenterY, 0,
          cardCenterX, cardCenterY, glowRadius
        );
        const intensity = 0.1 + state.clickBoost * 0.2;
        cardGlow.addColorStop(0, `rgba(0, 81, 135, ${intensity})`);
        cardGlow.addColorStop(0.5, `rgba(77, 130, 188, ${intensity * 0.3})`);
        cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)');
        ctx.fillStyle = cardGlow;
        ctx.fillRect(0, 0, width, height);
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    // Inicializar nodos
    stateRef.current.nodes = createNodes();
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      window.removeEventListener('resize', setup);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cardRect]);
  
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0
      }}
    />
  );
});

// Logo Dimoauto SVG como fallback
const DimoautoLogoSVG = () => (
  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60px'}}>
    <span style={{fontSize: '32px', fontWeight: 800, color: '#1f2a37', letterSpacing: '-1px'}}>Dimo</span>
    <span style={{fontSize: '32px', fontWeight: 800, color: 'white', background: '#2989d8', padding: '2px 12px', marginLeft: '0px', letterSpacing: '-1px', borderRadius: '4px'}}>auto</span>
  </div>
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [cardRect, setCardRect] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  const cardRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Cargar credenciales guardadas
  useEffect(() => {
    // Se recuerda el CORREO, nunca la contraseña. Guardar contraseñas en claro
    // en el navegador era el agujero más tonto que tenía el sistema; si alguna
    // quedó de antes, se borra al pasar por aquí.
    localStorage.removeItem('centro-remember-password');
    const savedEmail = localStorage.getItem('centro-remember-email');
    const savedRemember = localStorage.getItem('centro-remember-me');

    if (savedRemember === 'true' && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);
  
  // Actualizar posición de la tarjeta
  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setCardRect({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height
        });
      }
    };
    
    updateCardRect();
    window.addEventListener('resize', updateCardRect);
    window.addEventListener('scroll', updateCardRect);
    
    const timer = setTimeout(updateCardRect, 100);
    
    return () => {
      window.removeEventListener('resize', updateCardRect);
      window.removeEventListener('scroll', updateCardRect);
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Disparar efecto de pulso
    if (canvasRef.current?.triggerPulse) {
      canvasRef.current.triggerPulse();
    }

    try {
      const result = await login(email, password);

      if (result.success) {
        // Guardar o limpiar credenciales
        if (rememberMe) {
          localStorage.setItem('centro-remember-email', email);
          localStorage.setItem('centro-remember-me', 'true');
        } else {
          localStorage.removeItem('centro-remember-email');
          localStorage.removeItem('centro-remember-me');
        }
        localStorage.removeItem('centro-remember-password');
        onLogin(result.user);
      } else {
        setError(result.error);
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Error de conexión. Por favor, intenta de nuevo.');
      setLoading(false);
    }
  };

  // Función para recuperar contraseña
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    
    if (!resetEmail) {
      setResetMessage('error:Por favor ingresa tu email');
      return;
    }
    
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: resetEmail })
      });
      
      if (response.ok) {
        setResetMessage('success:✅ Email de recuperación enviado. Revisa tu bandeja de entrada.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage('error:No se pudo enviar el email. Verifica que el email sea correcto.');
      }
    } catch (error) {
      setResetMessage('error:Error de conexión. Intenta de nuevo más tarde.');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: `1.5px solid ${C.line}`,
    fontSize: '13px',
    background: C.bg,
    color: C.ink,
    outline: 'none',
    boxSizing: 'border-box'
  };

  // Etiqueta del sistema: versalitas pequeñas y apretadas sobre el campo.
  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: C.muted,
    marginBottom: '5px'
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: C.bg,
      padding: '16px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Fondo animado Canvas */}
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      
      <div 
        ref={cardRef}
        style={{
          width: '100%',
          maxWidth: '360px',
          background: 'rgba(255, 255, 255, 0.98)',
          borderRadius: 14,
          padding: '28px',
          boxShadow: '0 20px 60px rgba(15,42,70,0.35)',
          border: '1px solid rgba(255,255,255,0.1)',
          position: 'relative',
          zIndex: 1,
          backdropFilter: 'blur(10px)'
        }}>
        {/* Logo y título */}
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <div style={{
            width: '160px',
            height: '55px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px'
          }}>
            {!logoError ? (
              <img 
                src="/logotipo.png" 
                alt="Dimoauto" 
                style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}} 
                onError={() => setLogoError(true)}
              />
            ) : (
              <DimoautoLogoSVG />
            )}
          </div>
          <h1 style={{color: C.ink, fontSize: 19, fontWeight: 800, letterSpacing: -0.2, margin: '0 0 4px 0'}}>
            Centro de Aplicaciones
          </h1>
          <p style={{color: C.muted, fontSize: 12.5, margin: 0}}>
            Grupo Dimoauto — Acceso unificado
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            background: `${C.rojo}18`,
            border: `1px solid ${C.rojo}44`,
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: '14px'
          }}>
            <span style={{color: C.rojo, fontSize: '12px'}}>⚠️ {error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '14px'}}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@dimoauto.com"
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = C.brand}
              onBlur={(e) => e.target.style.borderColor = C.line}
            />
          </div>

          <div style={{marginBottom: '12px'}}>
            <label style={labelStyle}>Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = C.brand}
              onBlur={(e) => e.target.style.borderColor = C.line}
            />
          </div>

          {/* Checkbox Recordar y Olvidé contraseña */}
          <div style={{marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11px', color: C.muted}}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{cursor: 'pointer', width: '14px', height: '14px'}}
              />
              Recordar mi correo
            </label>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'none',
                border: 'none',
                color: C.brand,
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '11px',
              background: loading ? C.faint : C.brand,
              border: 'none',
              borderRadius: 10,
              color: '#fff',
              fontSize: '13px',
              fontWeight: 800,
              boxShadow: loading ? 'none' : '0 3px 10px rgba(0,81,135,.28)',
              cursor: loading ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span style={{fontSize: '14px'}}>🔐</span>
            {loading ? 'Verificando...' : 'Iniciar Sesión'}
          </button>
        </form>

        {/* Footer */}
        <div style={{
          marginTop: '16px',
          padding: '10px',
          background: `${C.verde}14`,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: C.verde,
            animation: 'pulse 2s infinite'
          }}></span>
          <span style={{fontSize: '10px', fontWeight: 700, color: C.verde}}>Conexión segura con Supabase Auth</span>
        </div>
      </div>

      {/* Modal Olvidé Contraseña */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15,42,70,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: C.card,
            borderRadius: 14,
            width: '100%',
            maxWidth: '340px',
            boxShadow: '0 20px 60px rgba(15,42,70,0.35)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: C.brand,
              padding: '14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{margin: 0, color: '#fff', fontSize: 14, fontWeight: 800, letterSpacing: 0.2}}>
                🔑 Recuperar Contraseña
              </h3>
              <button
                onClick={() => { setShowForgotPassword(false); setResetMessage(''); setResetEmail(''); }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleForgotPassword} style={{padding: '18px'}}>
              <p style={{fontSize: 12.5, color: C.muted, marginBottom: '14px'}}>
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="tu@email.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1.5px solid ${C.line}`,
                  fontSize: '13px',
                  color: C.ink,
                  outline: 'none',
                  marginBottom: '12px',
                  boxSizing: 'border-box'
                }}
              />
              
              {resetMessage && (
                <div style={{
                  padding: '10px',
                  borderRadius: 10,
                  marginBottom: '12px',
                  background: resetMessage.startsWith('success') ? `${C.verde}18` : `${C.rojo}18`,
                  border: `1px solid ${resetMessage.startsWith('success') ? `${C.verde}44` : `${C.rojo}44`}`,
                  color: resetMessage.startsWith('success') ? C.verde : C.rojo,
                  fontSize: '11px',
                  fontWeight: 700
                }}>
                  {resetMessage.split(':')[1]}
                </div>
              )}
              
              <div style={{display: 'flex', gap: '10px'}}>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setResetMessage(''); setResetEmail(''); }}
                  className="fg-nav"
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: 10,
                    border: `1px solid ${C.line}`,
                    background: '#fff',
                    color: C.page,
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: 10,
                    border: 'none',
                    background: C.brand,
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 800,
                    boxShadow: '0 3px 10px rgba(0,81,135,.28)',
                    cursor: 'pointer'
                  }}
                >
                  Enviar enlace
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Animación pulse */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default Login;
